import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getInterestStageLabel } from '../leadsConfig';
import { LEAD_OFFER_CURRENCIES } from '../../services/leadsService';
import {
  LEADS_TEXT_SECONDARY,
  leadsDialogPaperSx,
  leadsDialogTitleSx,
  leadsGhostButtonSx,
  leadsPrimaryButtonSx,
  leadsTextFieldSx,
} from './leadsUi.styles';
import { panelCloseIconButtonSx } from '../../Map/components/PanelShell/PanelHeader';
import LeadInterestStockCheckbox from './LeadInterestStockCheckbox';

const currencyOptionSx = (active) => ({
  minWidth: 0,
  px: 1.5,
  height: 40,
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  borderRadius: '8px',
  border: '1px solid',
  borderColor: active ? '#489291' : 'rgba(255,255,255,0.12)',
  bgcolor: active ? 'rgba(72,146,145,0.18)' : 'transparent',
  color: active ? '#FFFFFF' : '#D4D4D4',
  '&:hover': { borderColor: '#489291', bgcolor: 'rgba(72,146,145,0.10)' },
});

/** "120.000" / "120000,5" → número; vacío → ''; inválido → NaN. */
export function parseOfferAmountInput(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const normalized = raw.replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return NaN;
  return Number(normalized);
}

/**
 * Confirma un cambio de etapa de interés que pide datos (V3-3):
 * - `ofertado`: monto + moneda (ARS / USD) y nota.
 * - otras (reservado / cerrado o salir de ellas): nota + checkbox de stock.
 * `stockLabel` vacío → sin checkbox.
 *
 * @param {{ open: boolean, stage: string, itemLabel?: string, stockLabel?: string,
 *   initialAmount?: number|null, initialCurrency?: string|null, busy?: boolean,
 *   onCancel: () => void,
 *   onConfirm: (args: { offerAmount?: number|null, offerCurrency?: string,
 *     note: string, applyStock: boolean }) => void }} props
 */
export default function LeadInterestStageDialog({
  open,
  stage,
  itemLabel = '',
  stockLabel = '',
  initialAmount = null,
  initialCurrency = null,
  busy = false,
  onCancel,
  onConfirm,
}) {
  const isOffer = stage === 'ofertado';
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [note, setNote] = useState('');
  const [applyStock, setApplyStock] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAmount(initialAmount === null || initialAmount === undefined ? '' : String(initialAmount).replace('.', ','));
    setCurrency(LEAD_OFFER_CURRENCIES.includes(initialCurrency) ? initialCurrency : 'USD');
    setNote('');
    setApplyStock(false);
    setError('');
  }, [open, initialAmount, initialCurrency]);

  const stageLabel = getInterestStageLabel(stage);

  const handleConfirm = () => {
    const out = { note: note.trim(), applyStock: Boolean(stockLabel) && applyStock };
    if (isOffer) {
      const parsed = parseOfferAmountInput(amount);
      if (Number.isNaN(parsed)) { setError('Ingresá un monto válido (ej.: 120.000).'); return; }
      if (parsed === '') {
        // Sin monto: si había uno guardado se borra; si no, no se manda.
        if (initialAmount !== null && initialAmount !== undefined) out.offerAmount = null;
      } else {
        out.offerAmount = parsed;
        out.offerCurrency = currency;
      }
    }
    onConfirm(out);
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      PaperProps={{ sx: { ...leadsDialogPaperSx, width: 'min(420px, 95vw)' } }}
      aria-labelledby="lead-interest-stage-title"
    >
      <DialogTitle id="lead-interest-stage-title" sx={leadsDialogTitleSx}>
        {isOffer ? 'Registrar oferta' : `Pasar a ${stageLabel}`}
        <IconButton size="small" onClick={onCancel} disabled={busy} aria-label="Cerrar" sx={panelCloseIconButtonSx}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pb: 1 }}>
        {itemLabel ? (
          <Typography sx={{ color: LEADS_TEXT_SECONDARY, fontSize: '13px', mb: 1.5 }}>
            {itemLabel}
          </Typography>
        ) : null}
        {isOffer && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, pt: 0.75 }}>
            <TextField
              label="Monto"
              size="small"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setError(''); }}
              error={Boolean(error)}
              helperText={error || ' '}
              disabled={busy}
              autoFocus
              inputProps={{ inputMode: 'decimal', maxLength: 20, 'aria-label': 'Monto de la oferta' }}
              sx={{ ...leadsTextFieldSx, flex: 1 }}
            />
            <Box role="radiogroup" aria-label="Moneda" sx={{ display: 'flex', gap: 0.75 }}>
              {LEAD_OFFER_CURRENCIES.map((c) => (
                <Button
                  key={c}
                  role="radio"
                  aria-checked={currency === c}
                  onClick={() => setCurrency(c)}
                  disabled={busy}
                  sx={currencyOptionSx(currency === c)}
                >
                  {c}
                </Button>
              ))}
            </Box>
          </Box>
        )}
        <TextField
          label="Nota (opcional)"
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
          inputProps={{ maxLength: 4000 }}
          sx={{ ...leadsTextFieldSx, mt: isOffer ? 0 : 0.75 }}
        />
        <LeadInterestStockCheckbox
          label={stockLabel}
          checked={applyStock}
          disabled={busy}
          onChange={setApplyStock}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1 }}>
        <Button onClick={onCancel} disabled={busy} sx={leadsGhostButtonSx}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={handleConfirm}
          sx={leadsPrimaryButtonSx}
          startIcon={busy ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
